import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ProductPriceScaleIntervalDTO, ProductPriceScaleRequestDTO, ProductPriceScaleType } from '@iota/inh-core-v1-management-client';
import { KeysOfType } from '../../constants-and-enums/helper-types';
import { buildScaleTypeOptions } from '../../constants-and-enums/product-price-scale-type';
import { getNumberFormattedWithDecimalPlaces } from '../../utils';
import {
    checkIntervalBeginIsSmallerThanEnd,
    checkIsInteger,
    checkScaleTypeIsSelectable,
    hasIntervalGaps,
    nextIntervalBegin,
    PriceScaleFormValue,
    priceScaleIntervalsValidator,
    sortIntervalsByBegin
} from '../../utils/price-scale-intervals.util';

type PriceScaleControls = Partial<Record<keyof ProductPriceScaleRequestDTO, FormControl | FormArray>>;

type PriceScaleIntervalControls = Record<keyof ProductPriceScaleIntervalDTO, FormControl>;

const INTERVAL_PROPERTIES_TO_FORMAT_WITH_AT_LEAST_TWO_DECIMAL_PLACES: KeysOfType<ProductPriceScaleIntervalDTO, number>[] = [
    'priceMonthly',
    'priceYearly'
];

@Component({
    standalone: false,
    selector: 'app-product-price-scale-form',
    templateUrl: './product-price-scale-form.component.html'
})
export class ProductPriceScaleFormComponent {
    readonly priceScale = input<PriceScaleFormValue | null>(null);
    readonly editMode = input(false);

    readonly formValid = output<boolean>();

    readonly scaleTypeOptions = computed(() => buildScaleTypeOptions(this.priceScale()?.scaleType));

    readonly gapWarning = $localize`There are gaps between the intervals.`;

    private readonly fb = inject(FormBuilder);

    private readonly hasGapsSignal = signal(false);
    readonly hasGaps = this.hasGapsSignal.asReadonly();

    readonly priceScaleForm = this.fb.group<PriceScaleControls>({
        scaleType: new FormControl<ProductPriceScaleType>(null, {
            validators: [Validators.required, checkScaleTypeIsSelectable]
        }),
        priceIntervals: this.fb.array([], { validators: [priceScaleIntervalsValidator, Validators.required] })
    });

    constructor() {
        this.priceScaleForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
            this.formValid.emit(this.priceScaleForm.valid);
            this.hasGapsSignal.set(hasIntervalGaps(this.priceIntervals.getRawValue()));
        });

        effect(() => this.initForm(this.priceScale(), this.editMode()));
    }

    private initForm(priceScale: PriceScaleFormValue | null, enableEditing: boolean): void {
        this.priceScaleForm.patchValue({ scaleType: priceScale?.scaleType ?? null });

        this.priceIntervals.clear();
        priceScale?.priceIntervals?.forEach((interval) => this.addInterval(interval, enableEditing));

        if (enableEditing) {
            this.priceScaleForm.enable();
        } else {
            this.priceScaleForm.disable();
        }

        this.hasGapsSignal.set(hasIntervalGaps(this.priceIntervals.getRawValue()));
    }

    get priceIntervals(): FormArray {
        return this.priceScaleForm?.controls?.priceIntervals as FormArray;
    }

    addInterval(interval?: ProductPriceScaleIntervalDTO, enableEditing = false): void {
        const intervalForm = this.fb.group<PriceScaleIntervalControls>(
            {
                intervalBegin: new FormControl<number>(interval?.intervalBegin ?? nextIntervalBegin(this.priceIntervals.getRawValue()), {
                    validators: [Validators.required, Validators.min(0), checkIsInteger]
                }),
                intervalEnd: new FormControl<number>(interval?.intervalEnd, {
                    validators: [Validators.required, Validators.min(0), checkIsInteger]
                }),
                priceMonthly: new FormControl<number>(interval?.priceMonthly, { validators: [Validators.required, Validators.min(0)] }),
                priceYearly: new FormControl<number>(interval?.priceYearly, { validators: [Validators.required, Validators.min(0)] })
            },
            { validators: [checkIntervalBeginIsSmallerThanEnd] }
        );

        if (!enableEditing) {
            for (const property of INTERVAL_PROPERTIES_TO_FORMAT_WITH_AT_LEAST_TWO_DECIMAL_PLACES) {
                intervalForm.get(property)?.setValue(getNumberFormattedWithDecimalPlaces(interval?.[property]));
            }
        }

        this.priceIntervals.push(intervalForm);
    }

    removeInterval(index: number): void {
        this.priceIntervals.removeAt(index);
    }

    getPriceScaleRequest(): ProductPriceScaleRequestDTO | null {
        if (!this.priceScaleForm.valid) return null;

        const { scaleType, priceIntervals } = this.priceScaleForm.getRawValue() as ProductPriceScaleRequestDTO;

        return { scaleType, priceIntervals: sortIntervalsByBegin(priceIntervals) };
    }
}
